<template>
  <ViewWrapper
    :breadcrumbs="{
      title: $t('product_events.CREATE'),
      backButton: { name: ROUTE_EVENTS },
    }"
  >
    <ProductFormLayout :key="cacheBust">
      <template #column-1>
        <div>
          <BaseTabs old-tabs :options="localeTabOptions">
            <template #default="{ activeTab: localeCode }">
              <div class="locale-container">
                <ProductLocaleDescription
                  :titles="formData.title"
                  :descriptions="formData.description_long"
                  :locales="companyLocales"
                  :selected-locale-code="localeCode"
                  @onUpdateFormLocaleTitle="updateFormLocaleTitle"
                  @onUpdateFormLocaleDescription="updateFormLocaleDescription"
                />
              </div>
            </template>
          </BaseTabs>
        </div>
      </template>

      <!-- Right Content (Desktop) -->
      <template #column-2>
        <ImageUpload @onImageUpload="updateFormData">
          <template #header-title>
            {{ $t('image.PRODUCT_IMAGE_FILE') }}
          </template>
          <template #header-tooltip-recommended-size>
            {{ $t('image.RECOMMENDED_SIZE') }}
          </template>
        </ImageUpload>

        <ProductSettings
          :product-type="'meet'"
          :no-score-value="formData.no_score"
          :manager-approval-value="formData.manager_approval"
          @onUpdateNoScore="updateNoScore"
          @onUpdateManagerApproval="updateFormDataManagerApproval"
        />
      </template>

      <Alert
        type="info"
        :title="$t('events.EVENT_DATES_NOW_IN_EVENTS_TITLE')"
        :description="$t('events.EVENT_DATES_NOW_IN_EVENTS_DESCRIPTION')"
        :heading-level="2"
      />

      <!-- Policies section -->
      <div class="policies-container">
        <ProductPolicies
          :title="$t('events.ACCESS')"
          @onChange="handleProductPolicyChange"
        />
      </div>

      <!-- Form action buttons -->
      <template #cancel-button>
        <router-link :to="{ name: ROUTE_EVENTS }">
          <BaseButton outline tabindex="-1" size="large">
            {{ $t('buttons.CANCEL') }}
          </BaseButton>
        </router-link>
      </template>

      <!-- Submit -->
      <template #submit-button>
        <BaseButton
          :loading="isSubmitting"
          :disabled="isSubmitting"
          data-cy="create-product"
          size="large"
          theme="green"
          @click="submitForm()"
        >
          {{ $t('buttons.CREATE') }}
        </BaseButton>
      </template>
    </ProductFormLayout>
    <!-- Modals -->
    <ModalUnsavedChanges
      v-if="displayUnsavedModal"
      :close-modal="hideUnsavedModal"
      :next="cachedNext"
    />
    <ModalCreatedEventSuccess
      v-if="displaySuccessModal"
      :policies="pickedPolicies"
      :product-id="productId"
    />
  </ViewWrapper>
</template>

<script>
import ModalCreatedEventSuccess from '@/components/events/ModalCreatedEventSuccess.vue'
import Alert from '@/components/generics/Alert.vue'
import ModalUnsavedChanges from '@/components/generics/ModalUnsavedChanges.vue'
import BaseButton from '@/components/generics/base/BaseButton.vue'
import BaseTabs from '@/components/generics/base/BaseTabs.vue'
import ImageUpload from '@/components/generics/imageUpload/ImageUpload.vue'
import ViewWrapper from '@/components/layout/view_wrapper/ViewWrapper.vue'
import ProductFormLayout from '@/components/product/ProductFormLayout.vue'
import ProductSettings from '@/components/product/form_components/ProductSettings.vue'
import ProductLocaleDescription from '@/components/product/product_components/ProductLocaleDescription.vue'
import ProductPolicies from '@/components/product/product_components/component_policies/ProductPolicies.vue'
import buildProductPayload from '@/helpers/buildProductPayload.js'
import { IS_CREATE_PRODUCT_PAGE, PRODUCT_ID } from '@/helpers/injectionKeys'
import { productTypeSettings } from '@/helpers/products/productTypes/productTypeSettings'
import { useCompanyLocale } from '@/hooks/useCompanyLocale'
import { ROUTE_EVENTS } from '@/router/routes/routeNames'
import ApiService from '@/service/api.service.js'
import { postProductToPolicy } from '@/service/v3/endpoints/policies.js'
import { provide, ref } from 'vue'

export default {
  components: {
    Alert,
    ModalCreatedEventSuccess,
    ProductPolicies,
    ProductSettings,
    ProductFormLayout,
    ViewWrapper,
    BaseButton,
    BaseTabs,
    ImageUpload,
    ModalUnsavedChanges,
    ProductLocaleDescription,
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasChanges && !this.isSubmitting && !this.isSubmitted) {
      this.cachedNext = next
      this.showUnsavedModal()
    } else {
      next()
    }
  },
  props: {},
  setup() {
    const productId = ref(undefined)
    provide(IS_CREATE_PRODUCT_PAGE, ref(true))
    provide(PRODUCT_ID, productId)

    const {
      contentLocaleDisplayOptions: localeTabOptions,
      supportedLocales: companyLocales,
      getContentLocaleCodes,
    } = useCompanyLocale()
    return {
      productId,

      localeTabOptions,
      companyLocales,
      getContentLocaleCodes,
    }
  },
  data() {
    return {
      ROUTE_EVENTS,
      cacheBust: 1,
      cachedNext: null,
      displaySuccessModal: false,
      displayUnsavedModal: false,
      isSubmitting: false,
      isSubmitted: false,
      formData: {
        title: {},
        description_long: {},
        content_link: null,
        no_score: false,
        no_skip: false,
        manager_approval: false,
      },
      content_links_localized: {},
      pickedPolicies: [],
      modalType: 'success',
    }
  },
  computed: {
    /* Initialize the locales based on company locales available */
    productDescriptions() {
      return this.companyLocales.reduce((object, locale) => {
        object[locale.code] = {
          title: '',
          descriptionShort: '',
          descriptionLong: '',
        }
        return object
      }, {})
    },
    hasPickedPolicies() {
      return this.pickedPolicies.length > 0
    },
    hasChanges() {
      return this.hasPickedPolicies
    },
  },

  methods: {
    handleProductPolicyChange(newValue) {
      this.pickedPolicies = newValue
    },

    updateNoScore(value) {
      this.formData.no_score = value
    },

    updateFormData(payload) {
      this.formData[payload.name] = payload.values
    },
    updateFormDataManagerApproval(status) {
      const formData = this.formData
      formData.manager_approval = status
      this.updateFormData(formData)
    },

    updateFormLocaleTitle({ locale, value }) {
      this.$set(this.formData.title, locale, value)
    },

    updateFormLocaleDescription({ locale, value }) {
      this.$set(this.formData.description_long, locale, value)
    },

    submitForm() {
      this.isSubmitting = true
      const config = {
        type: 'meet',
        settings: productTypeSettings.meet,
        localeCodes: this.getContentLocaleCodes(),
        data: this.formData,
      }
      const payload = buildProductPayload(config)
      this.uploadData(payload)
    },
    async uploadData(payload) {
      // prevent product from being created if no policies are added
      // needs to be done here because afterwards the product would already be created
      if (this.pickedPolicies.length === 0) {
        this.isSubmitting = false
        this.$toast(
          { text: this.$t('product.ERROR_PRODUCT_POLICY') },
          { displayDuration: 5000, theme: 'error' }
        )
        return
      }

      const createProductCall = await ApiService.createProduct(payload)
      if (createProductCall.error) {
        this.isSubmitting = false
        this.$toast(
          { text: this.$t('product.ERROR_PRODUCT_FORM') },
          { displayDuration: 5000, theme: 'error' }
        )
      } else {
        const productId = createProductCall.data.id
        this.productId = productId

        this.pickedPolicies.map((policy) =>
          postProductToPolicy({
            policyId: Number(policy.id),
            productId: Number(productId),
            policyState: policy.value,
          })
        )

        this.isSubmitting = false
        this.isSubmitted = true
        this.displaySuccessModal = true
      }
    },
    showUnsavedModal() {
      this.displayUnsavedModal = true
    },
    hideUnsavedModal() {
      this.displayUnsavedModal = false
    },
    resetForm() {
      this.cacheBust++
    },
    onPickedPolicies(pickedPolicies) {
      this.pickedPolicies = pickedPolicies
    },
  },
}
</script>

<style lang="scss" scoped>
.link-container {
  padding-bottom: $spacing-s;
}

.locale-container {
  display: flex;
  flex-direction: column;
  padding: $spacing-s;
  gap: $spacing-s;
}

.events-container {
  display: flex;
  flex-direction: column;
  height: 850px;
}

.policies-container {
  display: flex;
  flex-direction: column;
  height: 600px;
}
</style>
